import {updateCredits} from "./updateCredits";
import {
    startContentGeneratorProgressSimulation,
    stopContentGeneratorProgressSimulation,
    showGenerationSuccessAlert
} from "../components/contentGeneratorModal";
import {chatDebugLog} from "../helpers/chatDebug";

/**
 * Poll check_generation_status for async video generation in chat messages.
 *
 * When a chat message response contains is_async + generation_id, this module
 * polls the server until the video is ready, then updates the message element
 * in-place without a full page reload.
 */

// ── sessionStorage persistence (survive page reloads) ──

const PENDING_GEN_KEY = "growtype_chat_pending_video_gen_";

function storePendingGen(generationId, messageId, attempts, startTime) {
    try {
        sessionStorage.setItem(
            PENDING_GEN_KEY + generationId,
            JSON.stringify({
                generationId,
                messageId,
                attempts,
                startTime,
            })
        );
    } catch (e) {
        // sessionStorage may be unavailable
    }
}

function clearPendingGen(generationId) {
    try {
        sessionStorage.removeItem(PENDING_GEN_KEY + generationId);
    } catch (e) {
        // sessionStorage may be unavailable
    }
}

/**
 * Called on page load to resume any pending video generations.
 */
function resumePendingVideoGenerations(retryCount) {
    if (!sessionStorageAvailable()) return;
    retryCount = retryCount || 0;
    const now = Date.now();
    const maxTotalMs = 10 * 60 * 1000;
    let foundPending = false;

    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (!key || !key.startsWith(PENDING_GEN_KEY)) continue;
        foundPending = true;

        try {
            const data = JSON.parse(sessionStorage.getItem(key));
            const elapsed = now - (data.startTime || now);

            if (elapsed >= maxTotalMs) {
                clearPendingGen(data.generationId);
                continue;
            }

            // Find the message element in the chat
            const messageEl = $(`.g-chatform-message[data-id="${data.messageId}"]`);
            if (messageEl.length === 0) {
                // Retry DOM lookup up to 10 times (5s total)
                if (retryCount < 10) {
                    setTimeout(() => resumePendingVideoGenerations(retryCount + 1), 500);
                } else {
                    clearPendingGen(data.generationId);
                }
                continue;
            }

            // Resume polling — route through startVideoPolling so the guard flag,
            // loader, and progress bar are all correctly initialised.
            startVideoPolling(data.generationId, messageEl, data.startTime);
        } catch (e) {
            clearPendingGen(key ? key.slice(PENDING_GEN_KEY.length) : "");
        }
    }
}

function sessionStorageAvailable() {
    try {
        const test = "__test__";
        sessionStorage.setItem(test, test);
        sessionStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
}

// ── Main polling entry point ──

/**
 * Start polling for async video generation completion.
 *
 * @param {string} generationId  - The generation_id from the API response.
 * @param {jQuery} messageEl     - The message element to update on completion.
 */
export function startVideoPolling(generationId, messageEl, startedAt = Date.now()) {
    const messageId = messageEl.attr("data-id") || "";
    const startTime = Number(startedAt) || Date.now();

    // Guard: prevent duplicate pollers if called multiple times for the same message
    // (e.g., once directly from unlockContent.js and once via resumePendingVideoGenerationsFromDom)
    if (messageEl.attr("data-video-polling-active") === "1") {
        chatDebugLog('videoPoll',"[Poll:startVideoPolling] already active, skipping", generationId);
        return;
    }
    messageEl.attr("data-video-polling-active", "1");

    chatDebugLog('videoPoll',"[Poll:startVideoPolling]", generationId, "messageId:", messageId);

    // Immediately replace locked placeholder with generating loader
    showGeneratingLoaderOnMessage(messageEl);

    // Every generation surface uses the same staged progress simulation. A
    // resumed video keeps its original start time instead of restarting at 0%.
    startContentGeneratorProgressSimulation(messageEl, startTime);

    if (messageId) {
        storePendingGen(generationId, messageId, 0, startTime);
    }

    pollVideoGeneration(generationId, messageEl, 0, startTime);
}

/**
 * Ensure the generating loader is visible on the message element.
 *
 * Two cases:
 *  A) Server-rendered message: has .g-chatform-message-assets-wrapper — replace
 *     its content with the generating loader div.
 *  B) userMessageTemplate placeholder: already has .g-chatform-content-generating-loader
 *     injected by appendUserMessage — just make sure it isn't hidden (d-none).
 */
function showGeneratingLoaderOnMessage(messageEl) {
    // Priority: if a loader already exists anywhere in the message (server-rendered
    // with d-none), just reveal it — avoids injecting duplicate HTML.
    var existingLoader = messageEl.find(".g-chatform-content-generating-loader");
    if (existingLoader.length) {
        chatDebugLog('videoPoll',"[Poll:showLoader] found existing loader, revealing", messageEl.attr("data-id") || "(no id)");
        existingLoader.removeClass("d-none");
        return;
    }

    var assetsWrapper = messageEl.find(".g-chatform-message-assets-wrapper");
    if (assetsWrapper.length) {
        // Case A: assets wrapper present but no loader yet — inject from config or fallback.
        const chatRoot = messageEl.closest(".g-chatform");
        const chatId = chatRoot.attr("id");
        const loaderHtml = window.growtype_chat?.[chatId]?.content_generating_loader;
        chatDebugLog('videoPoll',"[Poll:showLoader] Case A (assetsWrapper) chatId:", chatId, "loaderHtml from config:", !!loaderHtml);
        if (loaderHtml) {
            assetsWrapper.html(loaderHtml);
            assetsWrapper.find(".g-chatform-content-generating-loader").removeClass("d-none");
        } else {
            assetsWrapper.html(
                '<div class="g-chatform-content-generating-loader"><div class="progress-circular"><svg viewBox="0 0 100 100"><circle class="bg" cx="50" cy="50" r="45"></circle><circle class="progress-bar" cx="50" cy="50" r="45"></circle></svg><div class="percent">0%</div></div><div class="loading-text">Generating video...</div></div>'
            );
        }
    } else {
        chatDebugLog('videoPoll',"[Poll:showLoader] Case B (no assetsWrapper, no existing loader) — nothing to inject for", messageEl.attr("data-id") || "(no id)");
    }
    // Case B (no assetsWrapper, no existing loader): nothing to show yet.
}

/**
 * The actual polling loop.
 */
function pollVideoGeneration(
    generationId,
    messageEl,
    initialAttempts,
    startTime
) {
    const maxAttempts = 120; // 10 minutes at 5s intervals
    let attempts = initialAttempts;
    let pollTimer;
    let activeRequest = null;

    function nextDelay() {
        // Fast initial feedback, then bounded backoff to reduce status traffic.
        return Math.min(15000, Math.round(2000 * Math.pow(1.45, Math.min(attempts, 6))));
    }

    function stopIfDetached() {
        if (!messageEl[0] || !document.documentElement.contains(messageEl[0])) {
            clearTimeout(pollTimer);
            if (activeRequest && typeof activeRequest.abort === "function") {
                activeRequest.abort();
            }
            messageEl.removeAttr("data-video-polling-active");
            return true;
        }
        return false;
    }

    function poll() {
        if (stopIfDetached()) return;
        attempts++;
        const elapsed = Date.now() - startTime;

        chatDebugLog('videoPoll',`[Poll:poll] genId=${generationId} attempt=${attempts}/${maxAttempts} elapsed=${Math.round(elapsed / 1000)}s`);

        if (attempts > maxAttempts || elapsed >= 10 * 60 * 1000) {
            console.warn("[Poll:poll] TIMED OUT", generationId);
            clearTimeout(pollTimer);
            clearPendingGen(generationId);
            cleanupStatusText(messageEl);
            replacePlaceholderWithText(
                messageEl,
                "Video generation timed out. Please try again."
            );
            return;
        }

        const statusText = messageEl.find(".async-video-status-text");
        if (statusText.length) {
            statusText.text("Generating video...");
        }

        // Persist progress for page reload resilience
        const messageId = messageEl.attr("data-id") || "";
        const sessionToken = messageEl.closest(".g-chatform").attr("data-session-token") || "";
        storePendingGen(generationId, messageId, attempts, startTime);

        activeRequest = jQuery.ajax({
            url: growtype_chat_ajax.url,
            type: "post",
            data: {
                generation_id: generationId,
                message_id: messageId,
                session_token: sessionToken,
                action: growtype_chat_ajax.check_generation_status,
                nonce: growtype_chat_ajax.nonce,
            },
            success: function (response) {
                chatDebugLog('videoPoll',"[Poll:poll] AJAX response genId:", generationId, "success:", response.success, "status:", response.data?.status);
                if (!response.success) {
                    console.error("[Poll:poll] response.success=false, stopping", response);
                    clearTimeout(pollTimer);
                    clearPendingGen(generationId);
                    return;
                }

                const statusData = response.data;
                const statusValue = statusData.status || "processing";

                if (statusValue === "completed" || statusValue === "succeeded") {
                    chatDebugLog('videoPoll',"[Poll:poll] COMPLETED genId:", generationId, "videoUrls:", statusData.video_urls);
                    clearTimeout(pollTimer);
                    clearPendingGen(generationId);
                    cleanupStatusText(messageEl);
                    messageEl.removeAttr("data-video-polling-active");

                    // Stop the generating loader and show success
                    stopContentGeneratorProgressSimulation(messageEl);

                    // Replace placeholder with the actual video
                    const videoUrls = statusData.video_urls || [];
                    if (videoUrls.length > 0) {
                        updateMessageWithVideo(messageEl, videoUrls);
                    } else {
                        console.warn("[Poll:poll] completed but video_urls is empty", statusData);
                        replacePlaceholderWithText(
                            messageEl,
                            "Video ready! Check your gallery."
                        );
                    }

                    showGenerationSuccessAlert();

                    if (statusData.existing_credits !== undefined) {
                        updateCredits(statusData.existing_credits);
                    }
                } else if (statusValue === "failed" || statusValue === "error") {
                    console.error("[Poll:poll] FAILED genId:", generationId, "error:", statusData.error);
                    clearTimeout(pollTimer);
                    clearPendingGen(generationId);
                    cleanupStatusText(messageEl);
                    replacePlaceholderWithText(
                        messageEl,
                        statusData.error || "Video generation failed. Please try again."
                    );
                } else {
                    // Still processing — poll again
                    const delay = nextDelay();
                    console.log(`[Poll:poll] still processing, next poll in ${delay}ms`);
                    pollTimer = setTimeout(poll, delay);
                }
            },
            error: function (xhr, status, err) {
                if (status === "abort") return;
                // Retry on network errors
                const delay = nextDelay();
                console.warn(`[Poll:poll] AJAX error, retrying in ${delay}ms`, status, err, xhr.responseText);
                pollTimer = setTimeout(poll, delay);
            },
            complete: function () {
                activeRequest = null;
            },
        });
    }

    // Start polling immediately
    poll();
}

/**
 * Replace the placeholder in the message element with the actual video.
 */
function updateMessageWithVideo(messageEl, videoUrls) {
    const assetsWrapper = messageEl.find(".g-chatform-message-assets-wrapper");

    // Prefer server-rendered HTML (fancybox, autoplay, etc.)
    const rendered = videoUrls.filter((v) => v.html).map((v) => v.html);
    if (rendered.length > 0) {
        const html = `<div class="g-chatform-message-assets-wrapper" data-amount="${
            videoUrls.length
        }">${rendered.join("")}</div>`;
        if (assetsWrapper.length) {
            assetsWrapper.replaceWith(html);
        } else {
            messageEl.find(".g-chatform-message-content-inner").html(html);
        }
        revealMessageElement(messageEl);
        return;
    }

    // Fallback
    if (videoUrls.length === 1) {
        const video = videoUrls[0];
        const videoUrl = video.url || "";
        const videoHtml =
            `<div class="g-chatform-message-video-wrapper">` +
            `<video controls class="g-chatform-message-video" style="width:100%;max-height:400px">` +
            `<source src="${videoUrl}" type="video/mp4"></video></div>`;

        if (assetsWrapper.length) {
            assetsWrapper.replaceWith(videoHtml);
        } else {
            messageEl.find(".g-chatform-message-content-inner").html(videoHtml);
        }
    } else {
        let html = '<div class="row g-2">';
        const colClass = "col-6";
        videoUrls.forEach((video) => {
            const url = video.url || "";
            if (url) {
                html +=
                    `<div class="${colClass}">` +
                    `<div class="g-chatform-message-video-wrapper">` +
                    `<video controls class="g-chatform-message-video" style="width:100%;max-height:400px">` +
                    `<source src="${url}" type="video/mp4"></video></div></div>`;
            }
        });
        html += "</div>";
        if (assetsWrapper.length) {
            assetsWrapper.replaceWith(html);
        } else {
            messageEl.find(".g-chatform-message-content-inner").html(html);
        }
    }

    revealMessageElement(messageEl);
}

/**
 * Show the inner parts of a message element hidden by resetUserMessageTemplate.
 * Called after video injection when renderMessage was bypassed for async video.
 */
function revealMessageElement(messageEl) {
    messageEl.find(".g-chatform-message-content-inner").show();
    messageEl.find(".g-chatform-message-inner").show();
    messageEl.find(".g-chatform-message-actions").show();

    // Hide the quick-action regenerate button — doesn't apply to video
    messageEl.find(".g-chatform-message-actions-quick").hide();

    // Init lazy-load on the newly injected video elements
    const lazyEls = messageEl.find(".lazy-load");
    if (lazyEls.length && typeof jQuery.fn.Lazy === "function") {
        lazyEls.Lazy();
    }

    // Init fancybox on image-wrappers that carry data-fancybox-settings
    messageEl.find(".image-wrapper[data-fancybox-settings]").each(function () {
        const $el = jQuery(this);
        const settingsRaw = $el.attr("data-fancybox-settings");
        if (!settingsRaw) return;
        if (typeof $el.fancybox !== "function") return;
        try {
            $el.fancybox(JSON.parse(settingsRaw));
        } catch (e) {
            // ignore
        }
    });
}

/**
 * Remove the async-video-status-text element.
 */
function cleanupStatusText(messageEl) {
    const statusText = messageEl.find(".async-video-status-text");
    if (statusText.length) {
        statusText.remove();
    }
}

/**
 * Replace the assets-wrapper placeholder with a simple text message.
 * Also reveals the message element in case renderMessage was bypassed.
 */
function replacePlaceholderWithText(messageEl, text) {
    const assetsWrapper = messageEl.find(".g-chatform-message-assets-wrapper");
    const html = `<div class="g-chatform-message-text">${text}</div>`;
    if (assetsWrapper.length) {
        assetsWrapper.replaceWith(html);
    } else {
        messageEl.find(".g-chatform-message-content-inner").html(html);
    }
    revealMessageElement(messageEl);
}

// ── Resume on page load ──

function resumePendingVideoGenerationsFromDom() {
    if ($(".g-chatform-message").length > 0) {

        var found = 0;
        $(".g-chatform-message").each(function () {
            var $msg = $(this);
            var msgId = $msg.attr("data-id") || "(no id)";
            // Skip messages where polling is already active
            if ($msg.attr("data-video-polling-active") === "1") {
                return;
            }
            var isPending = $msg.find('[data-asset-url="generate_pending"]').length > 0;
            if (!isPending) return;
            // Skip messages that still have a lock/unlock button — the video is locked
            // and must not be polled until the user pays and clicks unlock.
            if ($msg.find(".btn-unlock-content").length > 0) {
                chatDebugLog('videoPoll',"[VideoPoll:Resume] msg", msgId, "still locked (btn-unlock-content found), skipping");
                return;
            }
            var $wrapper = $msg.find(".image-wrapper[data-generation-id]").first();
            var genId = $wrapper.attr("data-generation-id") || $msg.attr("data-generation-id");
            if (!genId) {
                chatDebugLog('videoPoll',"[VideoPoll:Resume] msg", msgId, "has generate_pending but no generation-id, skipping");
                return;
            }
            found++;
            chatDebugLog('videoPoll',"[VideoPoll:Resume] starting poll for msg", msgId, "genId:", genId);
            startVideoPolling(genId, $msg);
        });

        if (found > 0) {
            chatDebugLog('videoPoll',"[VideoPoll:Resume] Resumed", found, "pending video generation(s)");
        } else {
            chatDebugLog('videoPoll',"[VideoPoll:Resume] no pending unlocked videos found");
        }
    }
}

$(function () {
    resumePendingVideoGenerations(0);
    resumePendingVideoGenerationsFromDom();
});

$(window).on("pageshow", function (event) {
    if (event.originalEvent && event.originalEvent.persisted) {
        resumePendingVideoGenerations(0);
        resumePendingVideoGenerationsFromDom();
    }
});

document.addEventListener("growtypeChatContentRendered", function () {
    resumePendingVideoGenerationsFromDom();
});
document.addEventListener("growtypeChatVideoPendingScan", function () {
    resumePendingVideoGenerationsFromDom();
});
